import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Copy, CheckCircle, Share2, Send, Users, Building2, Loader2 } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { api } from '@/lib/api/client';
import { SCOUT_POINT_VALUES } from '@tendedloop/shared';
import { formatNumber } from '@/utils/formatDate';
import { formatDate } from '@/utils/formatDate';

interface ReferralItem {
  id: string;
  referredEmail: string;
  type: string;
  status: string;
  xpAwarded: number;
  note: string | null;
  createdAt: string;
  convertedAt: string | null;
}

interface ReferralStats {
  totalInvited: number;
  totalConverted: number;
  totalCompanyReferred: number;
  totalCompanyConverted: number;
  totalXpEarned: number;
}

export default function ReferralsPage() {
  const { t } = useTranslation('scout');
  const navigate = useNavigate();
  const { scout, isAuthenticated } = useAuthStore();
  const [copied, setCopied] = useState(false);
  const [stats, setStats] = useState<ReferralStats | null>(null);
  const [referrals, setReferrals] = useState<ReferralItem[]>([]);
  const [loadError, setLoadError] = useState(false);

  // Invite form
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);

  // Company referral form
  const [companyEmail, setCompanyEmail] = useState('');
  const [companyNote, setCompanyNote] = useState('');
  const [referring, setReferring] = useState(false);
  const [referSuccess, setReferSuccess] = useState(false);
  const [referError, setReferError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
      return;
    }
    Promise.allSettled([
      api.get<ReferralStats>('/scout/referrals/stats').then(setStats),
      api.get<ReferralItem[]>('/scout/referrals').then(setReferrals),
    ]).then((results) => {
      if (results.every((r) => r.status === 'rejected')) setLoadError(true);
    });
  }, [isAuthenticated, navigate]);

  if (!scout) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  const referralLink = `${window.location.origin}/login?ref=${scout.referralCode}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API not available
    }
  };

  const shareLink = async () => {
    const canShare = typeof navigator.share === 'function';
    window.gtag?.('event', 'referral_share', { method: canShare ? 'native' : 'clipboard' });
    if (canShare) {
      try {
        await navigator.share({
          title: t('referrals.shareTitle'),
          text: t('referrals.shareLink'),
          url: referralLink,
        });
      } catch {
        // Share cancelled
      }
    } else {
      copyLink();
    }
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;

    setInviting(true);
    setInviteError(null);
    setInviteSuccess(false);

    try {
      await api.post('/scout/referrals/invite', { email: inviteEmail.trim() });
      window.gtag?.('event', 'scout_invite_sent', { type: 'scout' });
      setInviteSuccess(true);
      setInviteEmail('');
      // Refresh stats & referrals
      const [newStats, newReferrals] = await Promise.all([
        api.get<ReferralStats>('/scout/referrals/stats'),
        api.get<ReferralItem[]>('/scout/referrals'),
      ]);
      setStats(newStats);
      setReferrals(newReferrals);
      setTimeout(() => setInviteSuccess(false), 3000);
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('referrals.inviteError');
      setInviteError(msg);
    } finally {
      setInviting(false);
    }
  };

  const handleCompanyReferral = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!companyEmail.trim()) return;

    setReferring(true);
    setReferError(null);
    setReferSuccess(false);

    try {
      await api.post('/scout/referrals/company', {
        email: companyEmail.trim(),
        ...(companyNote.trim() ? { note: companyNote.trim() } : {}),
      });
      window.gtag?.('event', 'scout_invite_sent', { type: 'company' });
      setReferSuccess(true);
      setCompanyEmail('');
      setCompanyNote('');
      // Refresh stats & referrals
      const [newStats, newReferrals] = await Promise.all([
        api.get<ReferralStats>('/scout/referrals/stats'),
        api.get<ReferralItem[]>('/scout/referrals'),
      ]);
      setStats(newStats);
      setReferrals(newReferrals);
      setTimeout(() => setReferSuccess(false), 3000);
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('referrals.inviteError');
      setReferError(msg);
    } finally {
      setReferring(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary/5 to-white pb-8">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-4">
        <button onClick={() => navigate(-1)} className="p-2 rounded-full hover:bg-warm-faint">
          <ArrowLeft className="w-5 h-5 text-muted-foreground" />
        </button>
        <h1 className="text-section font-semibold text-foreground">{t('referrals.title')}</h1>
      </div>

      <div className="px-4 space-y-4">
        {/* Share Link */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-warm-border">
          <h3 className="text-small font-semibold text-foreground mb-2">{t('referrals.shareTitle')}</h3>
          <div className="flex items-center gap-2 mb-3">
            <div className="flex-1 bg-warm-faint px-3 py-2 rounded-lg text-caption font-mono text-muted-foreground truncate">
              {referralLink}
            </div>
            <button
              onClick={copyLink}
              className="p-2.5 rounded-lg hover:bg-warm-faint text-muted-foreground flex-shrink-0"
              aria-label={t('referrals.copyLink')}
            >
              {copied ? <CheckCircle className="w-5 h-5 text-primary" /> : <Copy className="w-5 h-5" />}
            </button>
          </div>
          <button
            onClick={shareLink}
            className="w-full h-11 rounded-xl bg-gradient-to-r from-primary to-forest text-white font-medium text-small flex items-center justify-center gap-2 active:scale-[0.98] transition-all"
          >
            <Share2 className="w-4 h-4" />
            {t('referrals.shareButton')}
          </button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="bg-white rounded-xl p-4 shadow-sm border border-warm-border">
            <h3 className="text-small font-semibold text-foreground mb-3">{t('referrals.stats')}</h3>
            <div className="grid grid-cols-3 gap-3">
              <div className="text-center">
                <p className="text-section font-bold text-foreground">{formatNumber(stats.totalInvited)}</p>
                <p className="text-caption text-muted-foreground">{t('referrals.scoutsInvited')}</p>
              </div>
              <div className="text-center">
                <p className="text-section font-bold text-foreground">{formatNumber(stats.totalCompanyConverted)}</p>
                <p className="text-caption text-muted-foreground">{t('referrals.companiesReferred')}</p>
              </div>
              <div className="text-center">
                <p className="text-section font-bold text-forest">{formatNumber(stats.totalXpEarned)}</p>
                <p className="text-caption text-muted-foreground">{t('referrals.totalXpEarned')}</p>
              </div>
            </div>
          </div>
        )}

        {/* Invite Scout Form */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-warm-border">
          <h3 className="text-small font-semibold text-foreground mb-1">{t('referrals.inviteTitle')}</h3>
          <p className="text-caption text-muted-foreground mb-3">
            {t('referrals.inviteSubtitle', { xp: SCOUT_POINT_VALUES.REFERRAL_BONUS })}
          </p>
          <form onSubmit={handleInvite} className="space-y-2">
            <label className="sr-only" htmlFor="invite-email">
              {t('referrals.fmEmail')}
            </label>
            <input
              id="invite-email"
              type="email"
              required
              autoComplete="email"
              inputMode="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder={t('referrals.fmEmail')}
              className="w-full h-11 px-3 rounded-lg border border-warm-border text-small focus:outline-none focus:ring-2 focus:ring-primary"
              disabled={inviting}
            />
            {inviteError && <p className="text-caption text-critical">{inviteError}</p>}
            {inviteSuccess && <p className="text-caption text-forest">{t('referrals.sentMessage')}</p>}
            <button
              type="submit"
              disabled={inviting || !inviteEmail.trim()}
              className="w-full h-11 rounded-xl bg-primary/5 text-forest font-medium text-small flex items-center justify-center gap-2 border border-forest/20 hover:bg-primary/10 disabled:opacity-50 transition-all"
            >
              {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {inviting ? t('referrals.sending') : t('referrals.sendInvite')}
            </button>
          </form>
        </div>

        {/* Company Referral Form */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-warm-border">
          <h3 className="text-small font-semibold text-foreground mb-1">{t('referrals.companyTitle')}</h3>
          <p className="text-caption text-muted-foreground mb-3">
            {t('referrals.companySubtitle', { xp: SCOUT_POINT_VALUES.COMPANY_REFERRAL_BONUS })}
          </p>
          <form onSubmit={handleCompanyReferral} className="space-y-2">
            <label className="sr-only" htmlFor="company-email">
              {t('referrals.fmEmail')}
            </label>
            <input
              id="company-email"
              type="email"
              required
              autoComplete="email"
              inputMode="email"
              value={companyEmail}
              onChange={(e) => setCompanyEmail(e.target.value)}
              placeholder={t('referrals.fmEmail')}
              className="w-full h-11 px-3 rounded-lg border border-warm-border text-small focus:outline-none focus:ring-2 focus:ring-primary"
              disabled={referring}
            />
            <div>
              <label className="sr-only" htmlFor="company-note">
                {t('referrals.notePlaceholder')}
              </label>
              <input
                id="company-note"
                type="text"
                value={companyNote}
                onChange={(e) => setCompanyNote(e.target.value)}
                placeholder={t('referrals.notePlaceholder')}
                className="w-full h-11 px-3 rounded-lg border border-warm-border text-small focus:outline-none focus:ring-2 focus:ring-primary"
                disabled={referring}
                maxLength={500}
              />
              {companyNote.length > 0 && (
                <p
                  className={`text-end text-caption mt-0.5 ${companyNote.length > 450 ? 'text-gold' : 'text-muted-foreground'}`}
                >
                  {companyNote.length}/500
                </p>
              )}
            </div>
            {referError && <p className="text-caption text-critical">{referError}</p>}
            {referSuccess && <p className="text-caption text-forest">{t('referrals.sentMessage')}</p>}
            <button
              type="submit"
              disabled={referring || !companyEmail.trim()}
              className="w-full h-11 rounded-xl bg-steel/5 text-steel font-medium text-small flex items-center justify-center gap-2 border border-steel/20 hover:bg-steel/10 disabled:opacity-50 transition-all"
            >
              {referring ? <Loader2 className="w-4 h-4 animate-spin" /> : <Building2 className="w-4 h-4" />}
              {referring ? t('referrals.sending') : t('referrals.sendReferral')}
            </button>
          </form>
        </div>

        {/* Referral History */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-warm-border">
          <h3 className="text-small font-semibold text-foreground mb-3">{t('referrals.history')}</h3>
          {loadError && <p className="text-small text-critical text-center py-4">{t('referrals.loadError')}</p>}
          {!loadError && referrals.length === 0 && (
            <p className="text-small text-muted-foreground text-center py-4">{t('referrals.noReferrals')}</p>
          )}
          {referrals.length > 0 && (
            <div className="space-y-2">
              {referrals.map((ref) => (
                <div
                  key={ref.id}
                  className="flex items-center justify-between py-2 border-b border-warm-faint last:border-0"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      {ref.type === 'SCOUT' ? (
                        <Users className="w-4 h-4 text-primary flex-shrink-0" />
                      ) : (
                        <Building2 className="w-4 h-4 text-steel flex-shrink-0" />
                      )}
                      <p className="text-small text-foreground truncate">{ref.referredEmail}</p>
                    </div>
                    <p className="text-caption text-muted-foreground ms-6">{formatDate(ref.createdAt)}</p>
                  </div>
                  <div className="flex-shrink-0 ms-2">
                    {ref.status === 'CONVERTED' ? (
                      <span className="text-caption font-medium text-forest bg-forest-wash px-2 py-1 rounded-full">
                        {t('referrals.joined')}
                      </span>
                    ) : (
                      <span className="text-caption font-medium text-gold bg-gold/5 px-2 py-1 rounded-full">
                        {t('referrals.pending')}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
